import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaEye, FaRegEdit, FaFlask } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { syncNicuOutbox } from "../../services/nicuSyncEngine";
import PageHeading from "../PageHeading";
import ConfirmActionModal from "../ConfirmDeleteModal";
import ExportOptions from "../ExportOptions";
import SmartDropdown from "../Shared/SmartDropdown";
import axios from "axios";
import { saveToCache, readFromCache } from "../../services/pathologyCache";
import TableSkeleton from "../TableSkeleton";
import NicuViewAdmissionModal from "./NicuViewAdmissionModal";
import NicuPathologySlipModal from "./NicuBill/NicuPathologySlipModal";
import NicuPrintLayout from "../NicuPrintLayout";
import NicuAdmissionPrintInfo from "./NicuAdmissionPrintInfo";

const api = axios.create({
  baseURL: "https://api.muktihospital.com/api/nicu",
  headers: {
    "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
  },
});

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const NicuAdmittedPatient = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [admissions, setAdmissions] = useState([]);
  const [displayedAdmissions, setDisplayedAdmissions] = useState([]);
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSlip, setShowSlip] = useState(false);
  const [slipPatient, setSlipPatient] = useState(null);
  const [printAdmission, setPrintAdmission] = useState(null);
  const itemsPerPage = 10;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const actionButtonClass =
    "size-7 rounded-full text-sm flex items-center justify-center transition-all duration-300";
  const DEBUG = process.env.NODE_ENV !== "production";

  // Define skeleton columns to match table structure
  const skeletonColumns = [
    { width: 20, height: 20, isCircle: false, className: "print-hidden-in-print" }, // Checkbox
    { width: 50, height: 20 }, // SL No
    { width: 100, height: 20 }, // Reg No
    { width: 150, height: 20 }, // Patient Name
    { width: 120, height: 20 }, // Admission Date
    { width: 150, height: 20 }, // Consultant
    { width: 80, height: 20 }, // Bed No
    { width: 100, height: 20 }, // Billing Type
    { width: 100, height: 20 }, // Status
    { width: 100, height: 20 },
    { width: 50, height: 20, className: "print-hidden-in-print" }, // Actions
  ];

  // Create refs object for all admissions
  const triggerRefs = useRef({});

  // Clean up unused refs
  useEffect(() => {
    Object.keys(triggerRefs.current).forEach((key) => {
      if (!admissions.some((admission) => admission.id === key)) {
        delete triggerRefs.current[key];
      }
    });
  }, [admissions]);


  const fetchAdmissions = useCallback(
  async (page, query) => {
    setLoading(true);
    try {
      const response = await api.get("/admission", {
        params: {
          page,
          limit: itemsPerPage,
          search: query,
        },
      });
      const { admissions } = response.data.data;
      if (!Array.isArray(admissions)) {
        throw new Error("Invalid response format: Expected array of admissions");
      }

      const mappedAdmissions = admissions.map((admission) => ({
        ...admission,
        id: admission.id,
        regNo: admission.regNo,
        patientName: admission.patientName,
        admissionDate: admission.admissionDate,
        consultantName: admission.consultantName,
        bedNo: admission.bedNo,
        billingType: admission.billingType,
        status: admission.status,
        specialNo: admission.specialNo,
        serviceType: admission.serviceType,
        fatherName: admission.fatherName,
        motherName: admission.motherName,
        relativeName: admission.relativeName,
        careOf: admission.careOf,
        age: admission.age,
        sex: admission.sex,
        religion: admission.religion,
        address: admission.address,
        dateOfBirth: admission.dateOfBirth,
        phoneNumber: admission.phoneNumber,
        admissionTime: admission.admissionTime,
        relativeMobileNumber: admission.relativeMobileNumber,
        relationship: admission.relationship,
        agentName: admission.agentName,
        wardCabin: admission.wardCabin,
        primaryDiagnosis: admission.primaryDiagnosis,
        employeeCode: admission.employeeCode,
        employeeName: admission.employeeName,
        dischargeCode: admission.dischargeCode,
      }));

      const total = response.data.data.pagination.totalRecords || admissions.length; 
      const totalPages = Math.ceil(total / itemsPerPage) || 1;

      setAdmissions(mappedAdmissions);
      setDisplayedAdmissions(mappedAdmissions);
      setTotalPages(totalPages);
      setTotalRecords(total);

      const cacheKey = `nicu_admissions_p${page}_${query || ""}`;
      setNicuOfflineActive(false);
      saveToCache(cacheKey, {
        admissions: mappedAdmissions,
        totalPages,
        totalRecords: total,
      });

      if (mappedAdmissions.length === 0 && page > 1) {
        setCurrentPage(page - 1);
      }
    } catch (err) {
      if (DEBUG) console.error("DEBUG: fetchAdmissions error", err);
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cacheKey = `nicu_admissions_p${page}_${query || ""}`;
        const cached = await readFromCache(cacheKey);
        if (cached && cached.admissions) {
          setAdmissions(cached.admissions);
          setDisplayedAdmissions(cached.admissions);
          setTotalPages(cached.totalPages || 1);
          setTotalRecords(cached.totalRecords || 0);
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved admissions.", { autoClose: 2000 });
        } else {
          setAdmissions([]);
          setDisplayedAdmissions([]);
          setTotalPages(1);
          setTotalRecords(0);
          setNicuOfflineActive(false);
          toast.info("Offline: this page isn't saved yet. Open it online once.", { autoClose: 3000 });
        }
      } else {
        toast.error("Failed to load admissions", { autoClose: 2000 });
        setAdmissions([]);
        setDisplayedAdmissions([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
    } finally {
      setLoading(false);
    }
  },
  []
);

  const handlePrintAdmission = useCallback(() => {
    const rows = displayedAdmissions.filter((a) => selectedRows.includes(a.id));
    if (rows.length === 0) {
      toast.info("Tick one patient on the left first, then choose Print Admission.", { autoClose: 3000 });
      return;
    }
    if (rows.length > 1) {
      toast.info("Please select only one patient to print.", { autoClose: 3000 });
      return;
    }
    setDropdownOpen(false);
    setPrintAdmission(rows[0]);
  }, [displayedAdmissions, selectedRows]);

  useEffect(() => {
    if (!printAdmission) return;
    const timer = setTimeout(() => {
      window.print();
      setTimeout(() => setPrintAdmission(null), 500);
    }, 350);
    return () => clearTimeout(timer);
  }, [printAdmission]);

const handleView = useCallback((admission) => {
    setSelectedAdmission(admission);
    setViewModalOpen(true);
    setActiveDropdownId(null);
  }, []);

  // Handle deletion
  const handleDelete = useCallback(
    async (admissionIds) => {
      const ids = Array.isArray(admissionIds) ? admissionIds : [admissionIds];
      if (!ids.length) {
        toast.error("No admissions selected", { autoClose: 2000 });
        return;
      }
      setLoading(true); // Set loading to true
      try {
        // if (DEBUG) console.log("DEBUG: handleDelete", { ids });
        await Promise.all(
          ids.map(async (id) => {
            await api.delete(`/admission/delete/${id}`);
          })
        );

        toast.success(
          `${ids.length} admission${ids.length > 1 ? "s" : ""} deleted`,
          { autoClose: 2000 }
        );

        const newPage = displayedAdmissions.length === ids.length && currentPage > 1
          ? currentPage - 1
          : currentPage;
        setSelectedRows([]);
        setPendingDeleteId(null);
        setDeleteModalOpen(false);
        await fetchAdmissions(newPage, debouncedSearchQuery);
      } catch (err) {
        if (DEBUG) console.error("DEBUG: handleDelete error", err);
        toast.error(
          `Failed to delete admission(s): ${err.response?.data?.error || err.message}`,
          { autoClose: 2000 }
        );
        await fetchAdmissions(currentPage, debouncedSearchQuery);
      } finally {
        setLoading(false); // Set loading to false
      }
    },
    [currentPage, debouncedSearchQuery, fetchAdmissions, displayedAdmissions]
  );

  // Open deletion confirmation
  const openDeleteConfirmation = useCallback((admissionId) => {
    if (!admissionId) {
      toast.error("No admission selected", { autoClose: 2000 });
      return;
    }
    // if (DEBUG) console.log("DEBUG: openDeleteConfirmation", { admissionId });
    setPendingDeleteId(admissionId);
    setDeleteModalOpen(true);
  }, []);

  // Handle edit
  const handleEdit = useCallback(
    (admission) => {
      if (!admission?.id) {
        toast.error("Invalid admission ID", { autoClose: 2000 });
        return;
      }
      const editAdmissionData = {
        ...admission,
        id: admission.id,
        regNo: admission.regNo,
        specialNo: admission.specialNo,
        serviceType: admission.serviceType,
        patientName: admission.patientName,
        fatherName: admission.fatherName,
        motherName: admission.motherName,
        relativeName: admission.relativeName,
        careOf: admission.careOf,
        age: admission.age,
        sex: admission.sex,
        religion: admission.religion,
        address: admission.address,
        dateOfBirth: admission.dateOfBirth,
        phoneNumber: admission.phoneNumber,
        admissionDate: admission.admissionDate,
        admissionTime: admission.admissionTime,
        relativeMobileNumber: admission.relativeMobileNumber,
        relationship: admission.relationship,
        consultantName: admission.consultantName,
        agentName: admission.agentName,
        wardCabin: admission.wardCabin,
        bedNo: admission.bedNo,
        primaryDiagnosis: admission.primaryDiagnosis,
        employeeCode: admission.employeeCode,
        employeeName: admission.employeeName,
        dischargeCode: admission.dischargeCode,
        billingType: admission.billingType,
        status: admission.status,
      };
      navigate("/nicu-admission", {
        state: { editAdmissionData },
      });
    },
    [navigate]
  );

  // Handle release
  const handleRelease = useCallback(
    async (admissionId, admissionRegNo) => {
      if (!admissionId) {
        toast.error("No admission selected", { autoClose: 2000 });
        return;
      }
      setLoading(true);
      try {
        if (admissionRegNo) {
          const check = await api.get(`/admission/release-check?regNo=${encodeURIComponent(admissionRegNo)}`);
          if (!check.data.canRelease) {
            toast.error(check.data.message || "Clear all bills before releasing.", { autoClose: 5000 });
            setLoading(false);
            setActiveDropdownId(null);
            return;
          }
        }
        await api.put(`/admission/edit/${admissionId}`, {
          status: "Released",
          dischargeCode: `R${Date.now()}`,
          dischargeDate: new Date().toISOString().split("T")[0],
        });
        toast.success("Admission status updated to Released", { autoClose: 2000 });
        await fetchAdmissions(currentPage, debouncedSearchQuery);
      } catch (err) {
        if (DEBUG) console.error("DEBUG: handleRelease error", err);
        toast.error(
          `Failed to update admission status: ${err.response?.data?.error || err.message}`,
          { autoClose: 2000 }
        );
      } finally {
        setLoading(false); // Set loading to false
      }
      setActiveDropdownId(null);
    },
    [currentPage, debouncedSearchQuery, fetchAdmissions]
  );

  // Handle single delete
  const handleSingleDelete = useCallback(
    (admissionId) => {
      openDeleteConfirmation(admissionId);
    },
    [openDeleteConfirmation]
  );

  // Fetch admissions on page/search change
  useEffect(() => {
    fetchAdmissions(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery, fetchAdmissions]);

  useEffect(() => {
    const onSynced = (e) => {
      const count = e.detail?.synced || 0;
      if (count > 0) {
        toast.success(`${count} offline record${count > 1 ? "s" : ""} synced.`, { autoClose: 2500 });
      }
      fetchAdmissions(currentPage, debouncedSearchQuery);
    };
    window.addEventListener("nicu-synced", onSynced);


    if (navigator.onLine) {
      syncNicuOutbox();
    }

    return () => window.removeEventListener("nicu-synced", onSynced);
  }, [currentPage, debouncedSearchQuery, fetchAdmissions]);


  

  // Reset page on search
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  // Ensure currentPage doesn't exceed totalPages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      // console.log(`Adjusting page from ${currentPage} to ${totalPages}`);
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      // console.log("No data, resetting to page 1");
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Toast for no search results
  useEffect(() => {
    if (debouncedSearchQuery && displayedAdmissions.length === 0 && !loading) {
      toast.info("No admissions found", { toastId: "no-admissions-toast", autoClose: 2000 });
    }
  }, [debouncedSearchQuery, displayedAdmissions, loading]);

  const handlePageChange = useCallback(
    (page) => {
      if (page > 0 && page <= totalPages) {
        // console.log(`Changing page to ${page}`);
        setCurrentPage(page);
        setSelectedRows([]);
      }
    },
    [totalPages]
  );

  const handleSelectAll = useCallback(
    (e) => {
      // if (DEBUG) console.log("DEBUG: handleSelectAll", { checked: e.target.checked });
      const validIds = displayedAdmissions
        .filter((admission) => admission.id != null)
        .map((admission) => admission.id);
      setSelectedRows(e.target.checked ? validIds : []);
    },
    [displayedAdmissions]
  );

  const handleRowSelect = useCallback((admissionId) => {
    if (admissionId == null) {
      if (DEBUG)
        console.warn("DEBUG: handleRowSelect: Invalid admissionId", { admissionId });
      return;
    }
    setSelectedRows((prev) =>
      prev.includes(admissionId)
        ? prev.filter((id) => id !== admissionId)
        : [...prev, admissionId]
    );
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  // Get status label and styling
  const getStatusLabel = (admission) => {
    switch (admission.status) {
      case "Released":
        return { label: "Released", className: "bg-green-100 text-green-700" };
      case "Admitted":
        return { label: "Admitted", className: "bg-orange-100 text-orange-600" };
      default:
        return { label: admission.status || "Admitted", className: "bg-gray-100 text-gray-600" };
    }
  };

  const headers = [
    "SL No",
    "Reg No",
    "Patient Name",
    "Admission Date",
    "BACKDATE",
    "Consultant",
    "Bed No",
    "Billing Type",
    "Status",
  ];

const dataMapper = (admission) => [
  (currentPage - 1) * itemsPerPage + displayedAdmissions.indexOf(admission) + 1,
  admission.regNo || "-",
  admission.patientName || "-",
  admission.admissionDate
    ? new Date(admission.admissionDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$1/$2/$3")
    : "-",
  admission.isDateChangeRequested
    ? `${admission.requestedDate ? new Date(admission.requestedDate).toLocaleDateString("en-GB") : "Requested"}${admission.requestedTime ? " " + admission.requestedTime : ""}`
    : "-",
  admission.consultantName || "-",
  admission.bedNo || "-",
  admission.billingType || "-",
  admission.status || "Admitted",
].join("\t");

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", url: "/" },
      { label: "NICU"},
      { label: "Admitted Patients" },
    ],
    []
  );

  const allVisibleSelected = useMemo(
    () =>
      displayedAdmissions.length > 0 &&
      displayedAdmissions.every((admission) => admission.id && selectedRows.includes(admission.id)),
    [displayedAdmissions, selectedRows]
  );

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Nicu Admitted Patients" breadcrumbs={breadcrumbs} />
      </div>
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Admitted Patient List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => openDeleteConfirmation(selectedRows)}
                className={`${actionButtonClass} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white`}
                aria-label="Delete selected admissions"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by reg no or patient name..."
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Search admissions by reg no or patient name"
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => navigate("/nicu-admission")}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
                aria-label="Admit new NICU patient"
              >
                Admit Patient
              </button>
              <ExportOptions
                data={displayedAdmissions}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Nicu Admitted Patient List"
                headers={headers}
                dataMapper={dataMapper}
                extraOptions={[
                  { label: "Print Admission", onClick: handlePrintAdmission },
                ]}
              />
            </div>
          </div>
        </div>
        <style>
          {`
            @media print {
              .print-hidden, .print-hidden-in-print {
                display: none !important;
              }
              .print-table {
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th, .print-table td {
                border: 1px solid #000;
                padding: 8px;
                text-align: left;
                font-size: 12px;
              }
              .print-table th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              .print-table tbody tr:nth-child(even) {
                background-color: #f9f9f9;
              }
            }
          `}
        </style>
        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table
              className="min-w-[1440px] w-full divide-y divide-gray-200 print-table"
              role="table"
            >
              <thead>
                <tr className="bg-[#FBFBFD]">
                  <th className="text-left print-hidden-in-print" scope="col">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="selectAll"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={allVisibleSelected}
                        disabled={displayedAdmissions.length === 0 || loading}
                        aria-label="Select all admissions on this page"
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="selectAll"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  {headers.map((header, idx) => (
                    <th
                      key={idx}
                      className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"
                      scope="col"
                    >
                      {header}
                    </th>
                  ))}
                  <th
                    className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print"
                    scope="col"
                  >
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} />
                ) : displayedAdmissions.length === 0 ? (
                  <tr className="print-hidden">
                    <td
                      colSpan={11}
                      className="text-center py-5 text-gray-500"
                      role="cell"
                    >
                      {searchQuery
                        ? "No admissions found matching your search."
                        : "No admissions available."}
                    </td>
                  </tr>
                ) : (
                  displayedAdmissions.map((admission, index) => {
                    const statusStyles = getStatusLabel(admission);
                    return (
                      <tr
                        key={admission.id}
                        className="border-b border-M-text-color/10 hover:bg-gray-100 border-collapse"
                        role="row"
                      >
                        <td
                          className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print"
                          role="cell"
                        >
                          <div className="relative left-3 -top-[2px]">
                            <input
                              type="checkbox"
                              id={`admission-${index}`}
                              className="hidden peer"
                              checked={selectedRows.includes(admission.id)}
                              onChange={() => handleRowSelect(admission.id)}
                              aria-label={`Select admission ${admission.regNo || "unknown"}`}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={`admission-${index}`}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                            ></label>
                          </div>
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.regNo || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.patientName || "-"}
                          {admission.isDateChangeRequested && (
                              <span className="ml-1.5 text-amber-600" title="Backdate correction requested">⚠</span>
                          )}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.admissionDate
                            ? new Date(admission.admissionDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              }).replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$1/$2/$3")
                            : "-"}
                        </td>
                        <td className="p-3 text-sm">
                            {admission.isDateChangeRequested ? (
                              <span
                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700"
                                title={`Requested: ${admission.requestedDate ? new Date(admission.requestedDate).toLocaleDateString("en-GB") : "--"} ${admission.requestedTime || ""}`}
                              >
                                {admission.requestedDate
                                  ? new Date(admission.requestedDate).toLocaleDateString("en-GB")
                                  : "Requested"}
                                {admission.requestedTime ? ` · ${admission.requestedTime}` : ""}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.consultantName || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.bedNo || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.billingType || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles.className}`}
                          >
                            {statusStyles.label}
                          </span>
                        </td>
                        <td
                          className="p-3 text-left relative print-hidden-in-print"
                          role="cell"
                        >
                          <button
                            ref={(el) => {
                              if (admission.id) {
                                if (!triggerRefs.current[admission.id]) {
                                  triggerRefs.current[admission.id] = { current: null };
                                }
                                triggerRefs.current[admission.id].current = el;
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdownId(
                                activeDropdownId === admission.id ? null : admission.id
                              );
                            }}
                            className="size-7 rounded-full bg-gray-100 text-gray-600 text-sm inline-flex items-center justify-center hover:bg-gray-200 transition-all duration-300"
                            aria-label={`Open actions for admission ${admission.regNo || "unknown"}`}
                          >
                            <BsThreeDotsVertical />
                          </button>
                          <SmartDropdown
                            isOpen={activeDropdownId === admission.id}
                            triggerRef={triggerRefs.current[admission.id] || { current: null }}
                            onClose={() => setActiveDropdownId(null)}
                            options={{ width: 150, align: "right" }}
                          >
                            <div>
                              <button onClick={() => handleView(admission)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                                  <FaEye className="text-blue-500" /> View Details
                               </button>
                              <button
                                onClick={() => {
                                 console.log("🔵 admission:", admission.regNo, admission.patientName);
                                  setSlipPatient({
                                    regNo: admission.regNo,
                                    patientName: admission.patientName,
                                    patientId: admission.patientId || null,
                                    age: admission.age,
                                    authorizedBy: admission.authorizedBy || null,
                                  });
                                  setShowSlip(true);
                                  setActiveDropdownId(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <FaFlask className="text-purple-600" /> Pathology Slip
                              </button>
                              <button
                                onClick={() => { setActiveDropdownId(null); setPrintAdmission(admission); }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <FaRegEdit className="text-gray-600" /> Print Admission
                              </button>
                              <button
                                onClick={() => handleEdit(admission)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <FaRegEdit className="text-M-primary-color" /> Edit
                              </button>
                              <button
                                onClick={() => handleSingleDelete(admission.id)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <RiDeleteBin6Line className="text-red-600" /> Delete
                              </button>
                              {admission.status !== "Released" && (
                                <button
                                  onClick={() => handleRelease(admission.id, admission.regNo)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <FaCheck className="text-green-600" /> Released
                                </button>
                              )}
                            </div>
                          </SmartDropdown>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && !loading && (
            <div className="p-4 flex justify-between items-center print-hidden">
              <p className="text-sm text-gray-600">
                Records: {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
                {totalRecords}
              </p>
              <nav aria-label="Pagination">
                <ul className="inline-flex items-center border border-M-text-color/20 rounded-md overflow-hidden">
                  <li>
                    <button
                      className={`table-pagination-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                      aria-disabled={currentPage === 1}
                    >
                      <MdKeyboardArrowLeft />
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i}>
                      <button
                        className={`table-pagination-item ${
                          currentPage === i + 1 ? "bg-blue-600 text-white" : ""
                        }`}
                        onClick={() => handlePageChange(i + 1)}
                        aria-label={`Go to page ${i + 1}`}
                        aria-current={currentPage === i + 1 ? "page" : undefined}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      className={`table-pagination-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                      aria-disabled={currentPage === totalPages}
                    >
                      <MdKeyboardArrowRight />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => {
          // if (DEBUG) console.log("DEBUG: Closing ConfirmActionModal");
          setDeleteModalOpen(false);
          setPendingDeleteId(null);
        }}
        onConfirm={() => handleDelete(pendingDeleteId || selectedRows)}
        ids={pendingDeleteId ? [pendingDeleteId] : selectedRows}
        entityName="admission"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          pendingDeleteId
            ? "Do you really want to delete this admission?"
            : selectedRows.length === 1
            ? "Do you really want to delete this admission?"
            : `Do you really want to delete ${selectedRows.length} admissions?`
        }
      />
      <NicuViewAdmissionModal
        isOpen={viewModalOpen} 
        onClose={() => setViewModalOpen(false)} 
        admission={selectedAdmission} 
        onEdit={handleEdit} 
        onDelete={handleSingleDelete} 
      />
      {printAdmission && (
        <NicuPrintLayout
          className="nicu-admission-print"
          title="NICU Admission Form"
          billInfo={<NicuAdmissionPrintInfo admission={printAdmission} />}
        />
      )}

      <NicuPathologySlipModal
        isOpen={showSlip}
        onClose={() => setShowSlip(false)}
        patient={slipPatient}
      />
    </div>
  );
};

export default NicuAdmittedPatient;